import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/stores/auth.store';
import request from '@/utils/request';

const { Title, Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
  captchaAnswer: string;
}

interface CaptchaChallenge {
  id: string;
  svg: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [form] = Form.useForm<LoginForm>();

  const refreshCaptcha = useCallback(async () => {
    try {
      const { data } = await request.get<CaptchaChallenge>('/auth/captcha');
      setCaptcha(data as unknown as CaptchaChallenge);
      form.setFieldsValue({ captchaAnswer: '' });
    } catch {
      message.error('验证码加载失败，请刷新页面');
    }
  }, [form]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }
    refreshCaptcha();
  }, [isAuthenticated, navigate, refreshCaptcha]);

  const handleSubmit = async (values: LoginForm) => {
    if (!captcha) {
      message.error('请等待验证码加载');
      return;
    }
    setLoading(true);
    try {
      await login(values.username, values.password, captcha.id, values.captchaAnswer);
      message.success('Login successful');
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const errorMsg =
        (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ??
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Login failed. Please check your credentials.';
      message.error(errorMsg);
      // 任何失败都刷新验证码 — 服务端已经消费掉了旧的
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Card
        style={{
          width: 400,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          borderRadius: 12,
        }}
        bordered={false}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 4 }}>
            Zero Admin
          </Title>
          <Text type="secondary">Sign in to the admin panel</Text>
        </div>
        <Form<LoginForm>
          form={form}
          name="login"
          onFinish={handleSubmit}
          autoComplete="off"
          size="large"
          layout="vertical"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: 'Please enter your username' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="Username"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="Password"
            />
          </Form.Item>
          <Form.Item
            name="captchaAnswer"
            rules={[{ required: true, message: '请输入验证码' }]}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <Input
                prefix={<SafetyOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="验证码"
                maxLength={4}
                style={{ textTransform: 'lowercase' }}
                onChange={(e) => form.setFieldsValue({ captchaAnswer: e.target.value })}
              />
              <div
                onClick={refreshCaptcha}
                title="点击刷新"
                style={{
                  position: 'relative',
                  width: 140,
                  flexShrink: 0,
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: '#f4efe3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {captcha ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: captcha.svg }}
                    style={{ display: 'flex', alignItems: 'center', height: '100%' }}
                  />
                ) : (
                  <ReloadOutlined style={{ color: '#bfbfbf' }} />
                )}
              </div>
            </div>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Sign In
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;
